import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box, Text } from '@primer/react';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2>{`Overview`}</h2>
    <p>{`Tooltips are often used to convey information. However, tooltips are rarely appropriate, and its misuse can result in a myriad of accessibility issues.
Always consider `}<strong parentName="p">{`not`}</strong>{` using a tooltip for an improved user experience.`}</p>
    <h2>{`Why?`}</h2>
    <ul>
      <li parentName="ul">{`Tooltips are hidden by default making it easy to miss, so they should never be used to convey critical information.`}</li>
      <li parentName="ul">{`Tooltips are never accessible on mobile devices.`}</li>
      <li parentName="ul">{`Tooltips should `}<strong parentName="li">{`never`}</strong>{` be set on non-interactive elements (for example, `}<inlineCode parentName="li">{`div`}</inlineCode>{`, `}<inlineCode parentName="li">{`span`}</inlineCode>{`, `}<inlineCode parentName="li">{`p`}</inlineCode>{`), and should only ever be set on interactive elements (for example, `}<inlineCode parentName="li">{`button`}</inlineCode>{`, `}<inlineCode parentName="li">{`a`}</inlineCode>{`). Tooltips on non-interactive elements are not accessible to keyboard users and screen reader users.`}</li>
    </ul>
    <h2>{`Guidelines`}</h2>
    <h3>{`For designers`}</h3>
    <ul>
      <li parentName="ul">{`Reserve tooltips for components like icon buttons.`}</li>
      <li parentName="ul">{`Keep your tooltip text minimal.`}</li>
      <li parentName="ul">{`Only include tooltips on other components as a last resort.`}</li>
      <li parentName="ul"><strong parentName="li">{`Never`}</strong>{` include tooltips on non-interactive components (`}<inlineCode parentName="li">{`div`}</inlineCode>{`, `}<inlineCode parentName="li">{`span`}</inlineCode>{`, `}<inlineCode parentName="li">{`p`}</inlineCode>{`).`}</li>
    </ul>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" alt="Icon buttons with tooltips for edit, favorite, and sponsor" src="https://user-images.githubusercontent.com/40274682/160182083-a97ba63f-a297-4613-bb52-d4a8ef10cc10.png" />
    <Caption mdxType="Caption">Use a tooltip to give a text label to an icon button</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" alt="Non-interactive text element activating a long and wordy tooltip on hover" src="https://user-images.githubusercontent.com/40274682/160182084-948a7969-9c84-4c08-8801-4d8a5253b1ce.png" />
    <Caption mdxType="Caption">Don't use tooltips on non-interactive elements</Caption>
  </Dont>
    </DoDontContainer>
    <Box mb={3} display="flex" alignItems="flex-start" alignItems="center" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box as="p" mt="0" mdxType="Box">
    <h4>Annotations</h4>If you have an appropriate tooltip in your design, annotate the tooltip carefully with the
    preferred type (whether it's a label or description) to ensure proper semantics.
  </Box>
  <img width="456" alt="Mock of an icon button and its tooltip with an annotation pointing to the tooltip with 'label' text" src="https://user-images.githubusercontent.com/40274682/160183578-4f66e628-c207-47a4-8520-00a37e26def4.png" />
    </Box>
    <h3>{`For developers`}</h3>
    <p>{`If you come across a design or a page with a tooltip on a static element (for example, `}<inlineCode parentName="p">{`div`}</inlineCode>{`, `}<inlineCode parentName="p">{`span`}</inlineCode>{`, `}<inlineCode parentName="p">{`p`}</inlineCode>{`), remove it. Consult a designer for an alternative way of conveying the information.`}</p>
    <p>{`If you determine that tooltips are appropriate for your use case, make sure to carefully follow guidelines set in `}<a parentName="p" {...{
        "href": "https://primer.style/view-components/components/alpha/tooltip"
      }}>{`Primer ViewComponents: Tooltip component`}</a>{`.`}</p>
    <h2>{`Alternatives to tooltips`}</h2>
    <h3>{`1. Persist the content`}</h3>
    <p>{`If possible, persist the content so it's always available rather than using a tooltip, which hides content by default. This ensures that the content is always discovered and surfaced to users regardless of device.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" alt="Information about how many discussions can be pinned added to the confirmation dialog" src="https://user-images.githubusercontent.com/40274682/160192785-13764229-7c42-4fa9-b752-b2ec741d4d4a.png" />
    <Caption mdxType="Caption">Find an appropriate permanent place to display additional information.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" alt="Information about how many discussions can be pinned within tooltip on non-interactive element" src="https://user-images.githubusercontent.com/40274682/160192791-2d69905a-bd1f-4508-9c0a-9e968b6d4ac3.png" />
    <Caption mdxType="Caption">Don't use a tooltip to add additional helpful information</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`2. Don't duplicate content`}</h3>
    <p>{`If the tooltip duplicates content that is already available on the page, remove it.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" alt="Pointer hovering over a link with no tooltip" src="https://user-images.githubusercontent.com/40274682/160201665-0f3bf527-fef7-4d79-b81b-af194385be5d.png" />
    <Caption mdxType="Caption">Remove any duplicate tooltip text.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" alt="Screenshot of tooltip with GitHub username, @inkblotty, on a static span that says @inkblotty" src="https://user-images.githubusercontent.com/40274682/160201667-d90e076d-7441-45f4-b0b6-620693921105.png" />
    <Caption mdxType="Caption">Don't set tooltips that duplicate the trigger element content.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`3. Use a modal`}</h3>
    <p>{`Consider using a modal, which is accessible for mobile users and allows you to structure content that may otherwise be crammed into a tooltip.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" alt="Screenshot of GitHub Actions page with a modal that conveys workflow file information about a runner" src="https://user-images.githubusercontent.com/40274682/160210527-2319e408-d433-407f-aac2-66eaf9e63246.png" />
    <Caption mdxType="Caption">Do use a pattern that is accessible to a larger number of users such as a modal</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" alt="Screenshot of GitHub Actions page using a tooltip on a static element to convey long workflow file information" src="https://user-images.githubusercontent.com/40274682/160210534-52361d76-8dad-480e-b7a8-eb9332370a53.png" />
    <Caption mdxType="Caption">Don't place long content inside a tooltip</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`4. Use a summary disclosure`}</h3>
    <p>{`Consider using a summary disclosure that is accessible for mobile users and also offers the ability to show or hide content.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="800" alt="Screenshot of GitHub pricing page that uses a summary disclosure to hide and show additional information on each point" src="https://user-images.githubusercontent.com/40274682/160211684-6c102dd6-4039-4c7b-8c52-eeeb71a27db5.png" />
    <Caption mdxType="Caption">Do use a pattern that is accessible to a larger number of users such as a summary disclosure</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="800" alt="Screenshot of GitHub pricing page using a tooltip to hide and show long descriptions on each point" src="https://user-images.githubusercontent.com/40274682/160211686-c0cc0dd5-8659-4e72-b9dd-d60369ffa1d7.png" />
    <Caption mdxType="Caption">Don't use tooltips on non-interactive elements</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Other`}</h3>
    <p>{`If you are unsure which alternative is more suited to your scenario and need help, consult a designer or the GitHub Accessibility team (if you are GitHub staff) for advice.`}</p>
    <h2>{`Additional resources`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://heydonworks.com/article/your-tooltips-are-bogus/"
        }}>{`Your tooltips are bogus`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/erblint-github/blob/main/docs/rules/accessibility/no-aria-label-misuse-counter.md#no-aria-label-misuse-counter"
        }}>{`erb lint rule: NoAriaLabelMisuseCounter`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      